import React, {
  BaseHTMLAttributes,
  CSSProperties,
  forwardRef,
  ReactElement,
  ReactNode,
  useContext,
} from "react";

import classNames from "classnames";

import { EntityGroupContext } from "./EntityGroup";
import { StyledEntityCard } from "./Styles";
import {
  PolymorphicComponentPropsWithRef,
  PolymorphicRef,
} from "../../utils/types";
import { Card, ICardProps } from "../Card";

export interface IEntityCardProps
  extends ICardProps,
    BaseHTMLAttributes<HTMLElement> {
  /** ClassName to add on Card */
  className?: string;
  /** Show as group of Cards on grid */
  isCollection?: boolean;
  /** Background color to override for Card */
  background?: string;
  /** CSSProperties to override for Card */
  style?: CSSProperties;
}

export type EntityCardProps<C extends React.ElementType> =
  PolymorphicComponentPropsWithRef<C, IEntityCardProps>;

export type EntityComponent = <C extends React.ElementType = typeof Card>(
  props: EntityCardProps<C>
) => ReactElement | ReactNode | null;

export const EntityCard: EntityComponent & { displayName?: string } =
  forwardRef(
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore - due to @node/types making ts more strict
    <C extends React.ElementType = typeof Card>(
      {
        children,
        isCollection,
        background,
        className,
        as,
        ...rest
      }: EntityCardProps<C>,
      ref?: PolymorphicRef<C>
    ) => {
      const { renderAs } = useContext(EntityGroupContext);
      const classSuffix = `c-entity-card-${renderAs}`;
      const paperType = renderAs === "grid" ? "paper-1" : "paper-2";
      const Component = as || Card;
      return (
        <StyledEntityCard
          as={Component}
          $renderAs={renderAs}
          $background={background}
          variant={paperType}
          className={classNames("c-entity-card", classSuffix, className)}
          $isCollection={isCollection}
          {...rest}
          ref={ref}
        >
          {children}
        </StyledEntityCard>
      );
    }
  );

EntityCard.displayName = "EntityCard";
